/**
 * DOCUMENTATION:
 * Component: UiUxPracticalProject
 * Purpose: A capstone educational page demonstrating how to synthesize UI/UX principles into a real-world checkout form.
 * * * Note for Junior Developers:
 * - A checkout form is where businesses make money. If your UX is confusing or your UI is broken, you lose sales.
 * - This project combines Semantic HTML (for screen readers), Z-Pattern layouts (for visual hierarchy),
 * and defensive design (for error handling).
 * - Remember: Placeholders are not labels! Always use explicit <label> tags.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
        padding: "15px",
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    textTransform: "uppercase",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function UiUxPracticalProject() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* UI State */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    /* Scroll Listener for FAB */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* Copy to Clipboard Handler */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ---------------- DATA DEFINITIONS ---------------- */

    // Project Requirements Checklist
    const projectRequirements = useMemo(() => [
        { feature: "Reduce Cognitive Load", description: "Use Gestalt proximity to group related inputs (e.g., First Name and Last Name side-by-side). Include clear <label> tags, not just placeholders." },
        { feature: "Accessible Error States", description: "Use 'aria-invalid' and high-contrast red borders to indicate errors. Provide an 'aria-live' region to announce errors to screen readers." },
        { feature: "Typographic Hierarchy", description: "Use scale and font-weight to draw the user's eye directly to the 'Order Total' and the primary 'Pay Now' button." },
        { feature: "System Feedback", description: "Add a loading spinner and disable the submit button upon click to prevent double-charging and reassure the user the system is working." },
    ], []);

    // Coding examples demonstrating the synthesized project
    const codingModules = useMemo(() => [
        {
            title: "Phase 1: Accessible HTML Architecture",
            code: `\n<form class="checkout-form" aria-labelledby="checkout-title">\n  <h2 id="checkout-title">Secure Checkout</h2>\n  \n  \n  <fieldset class="form-group">\n    <legend class="sr-only">Personal Information</legend>\n    \n    <div class="input-wrapper">\n      <label for="email">Email Address <span aria-hidden="true">*</span></label>\n      <input \n        type="email" \n        id="email" \n        required \n        aria-describedby="email-error"\n      />\n      \n      <span id="email-error" class="error-msg" aria-live="polite"></span>\n    </div>\n  </fieldset>\n\n  \n  <div class="checkout-footer">\n    <p class="total-price">Total: $99.00</p>\n    <button type="submit" class="btn btn--primary" id="submit-btn">\n      <span class="btn__text">Pay Now</span>\n      <span class="btn__spinner hidden" aria-hidden="true"></span>\n    </button>\n  </div>\n</form>`,
            desc: "This HTML is heavily engineered. `fieldset` groups related data. `aria-describedby` links the input to its specific error message so screen readers can announce exactly what went wrong."
        },
        {
            title: "Phase 2: Gestalt Layouts & Typography (CSS)",
            code: `:root {\n  --color-error: hsl(0, 84%, 60%);\n  --space-sm: 0.5rem;\n  --space-lg: 2rem;\n}\n\n.checkout-form {\n  display: flex;\n  flex-direction: column;\n  gap: var(--space-lg); /* Macro-spacing: separate large groups */\n}\n\n.input-wrapper {\n  display: flex;\n  flex-direction: column;\n  gap: var(--space-sm); /* Micro-spacing: tie label tightly to input */\n}\n\n.total-price {\n  font-size: clamp(1.5rem, 3vw, 2.5rem); /* Fluid typographic scale */\n  font-weight: 800;\n  color: #100720;\n}`,
            desc: "We use the Law of Proximity here. The `gap` inside `.input-wrapper` is very small, tying the label to its input. The `gap` on the `.checkout-form` is large, separating different sections to reduce cognitive load."
        },
        {
            title: "Phase 3: Defensive UX (Interactive States)",
            code: `/* 1. Clear Focus States for Keyboard Navigation */\ninput:focus-visible {\n  outline: none;\n  box-shadow: 0 0 0 3px rgba(218, 165, 32, 0.5);\n  border-color: goldenrod;\n}\n\n/* 2. Distinct Error States */\ninput[aria-invalid="true"] {\n  border-color: var(--color-error);\n  background-color: hsla(0, 84%, 60%, 0.05);\n}\n\n/* 3. Button Loading State */\n.btn.is-loading {\n  cursor: not-allowed;\n  opacity: 0.8;\n}\n.btn.is-loading .btn__text {\n  visibility: hidden;\n}\n.btn.is-loading .btn__spinner {\n  display: block;\n  /* (Spinner animation CSS omitted for brevity) */\n}`,
            desc: "A great UX anticipates user actions. We provide a highly visible focus ring for keyboard users, a red background tint for error states, and a disabled loading state to prevent impatient users from clicking 'Pay' twice."
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    PRACTICAL UI/UX PROJECT
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* ✅ Theory Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Synthesizing Design & Engineering
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            A high-converting checkout form is the perfect intersection of UI and UX. The <strong>UI</strong> must be clean, utilizing whitespace and clear typography to make the form feel effortless. The <strong>UX</strong> must be bulletproof—handling errors gracefully, accommodating screen readers, and providing clear system feedback when a payment is processing.
                        </Typography>

                        {/* ✅ Project Requirements Checklist */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Project Scope & Specifications</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Feature</div>
                                    <div>Engineering Strategy</div>
                                </ResponsiveDataRow>
                            )}
                            {projectRequirements.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod" }}>{item.feature}</Box>
                                    <Box sx={{ opacity: 0.9, fontSize: "0.9rem", lineHeight: 1.5 }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Coding Modules Section */}
                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            {/* Module Header & Action Buttons */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, lineHeight: 1.6 }}>{item.desc}</Typography>

                            {/* Code Block Presentation */}
                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/uiux-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
